import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { ActionDTO } from '@behavior/activity/dto/ActionDTO';
import { PersonDTO } from '@behavior/inventories/dto/PersonDTO';
import useAllActions from '@behavior/activity/getAllActions';
import useMarkActionAsCompleted from '@behavior/activity/useMarkActionAsCompleted';
import { useTitleDispatch } from '@components/TitleContextProvider';
import ErrorPage from '@src/pages/ErrorPage';
import { Activity } from '@src/routes';
import { Mode } from '@src/constants';

// — Palette: mirrors ActionDetailView. INK + monochrome chrome, with one
// colored accent per row carrying urgency. Every other surface is restrained.
const INK = '#111418';
const TEXT = '#1f2328';
const TEXT_SECONDARY = '#5b6470';
const TEXT_MUTED = '#8b95a3';
const BORDER = '#e6e8ec';
const BORDER_HOVER = '#d4d8df';
const SURFACE = '#ffffff';
const SURFACE_SUBTLE = '#f7f8fa';

// Bucket accents — each only used for its row stripe + section header marker.
// The full pending range (overdue → today → this week) stays in the warm
// warning family so green is reserved exclusively for completion. Mirrors the
// detail view's StatusBanner palette (pending = amber, done = emerald).
const OVERDUE_ACCENT = '#dc2626';
const OVERDUE_FG = '#991b1b';
const TODAY_ACCENT = '#f59e0b';
const TODAY_FG = '#b45309';
const WEEK_ACCENT = '#fbbf24';
const WEEK_FG = '#92400e';
const UPCOMING_ACCENT = '#94a3b8';
const UPCOMING_FG = '#475569';
const DONE_ACCENT = '#10b981';
const DONE_FG = '#047857';

type Bucket = 'overdue' | 'today' | 'week' | 'upcoming' | 'completed';

const BUCKET_ORDER: Bucket[] = ['overdue', 'today', 'week', 'upcoming', 'completed'];

const BUCKET_META: Record<
  Bucket,
  { i18nKey: string; accent: string; fg: string }
> = {
  overdue: { i18nKey: 'action.list.section.overdue', accent: OVERDUE_ACCENT, fg: OVERDUE_FG },
  today: { i18nKey: 'action.list.section.dueToday', accent: TODAY_ACCENT, fg: TODAY_FG },
  week: { i18nKey: 'action.list.section.thisWeek', accent: WEEK_ACCENT, fg: WEEK_FG },
  upcoming: { i18nKey: 'action.list.section.upcoming', accent: UPCOMING_ACCENT, fg: UPCOMING_FG },
  completed: { i18nKey: 'action.list.section.completed', accent: DONE_ACCENT, fg: DONE_FG },
};

const maskPrivate = (s: string): string => s.replace(/[a-z]/g, 'x');

const fullName = (p?: PersonDTO, showPersonalData = true): string => {
  if (!p) return '';
  const raw =
    (p.name && p.name.trim()) ||
    [p.firstName, p.lastName].filter(Boolean).join(' ').trim() ||
    '';
  return showPersonalData ? raw : maskPrivate(raw);
};

const initials = (p?: PersonDTO): string => {
  if (!p) return '?';
  const first = (p.firstName?.[0] ?? p.name?.[0] ?? '').toUpperCase();
  const last = (p.lastName?.[0] ?? '').toUpperCase();
  return `${first}${last}` || '?';
};

const avatarColor = (p?: PersonDTO): string => {
  if (!p?.id) return '#dfe3ea';
  const palette = ['#e0e7ff', '#dcfce7', '#fef3c7', '#fce7f3', '#dbeafe', '#fee2e2', '#e6e8ec'];
  return palette[p.id % palette.length];
};

const startOfDay = (d: Date): Date => {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
};

const daysBetween = (target: Date, from: Date): number => {
  const ms = startOfDay(target).getTime() - startOfDay(from).getTime();
  return Math.round(ms / (1000 * 60 * 60 * 24));
};

const formatRelative = (d?: string | Date, locale?: string): string => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const days = daysBetween(dt, new Date());
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const months = Math.round(days / 30);
    if (Math.abs(months) >= 12) return rtf.format(Math.round(months / 12), 'year');
    if (Math.abs(days) >= 30) return rtf.format(months, 'month');
    return rtf.format(days, 'day');
  } catch {
    return '';
  }
};

const bucketFor = (action: ActionDTO): Bucket => {
  if (action.completionDte) return 'completed';
  if (!action.deadlineDte) return 'upcoming';
  const days = daysBetween(new Date(action.deadlineDte), new Date());
  if (days < 0) return 'overdue';
  if (days === 0) return 'today';
  if (days <= 7) return 'week';
  return 'upcoming';
};

type Filter = 'all' | 'pending' | 'completed';

// Inline `@keyframes` for the one-time stagger reveal. The animation is keyed
// off the row's index in the rendered list, capped at 24 so very long lists
// don't slow-burn.
const fadeUpKeyframe = {
  from: { opacity: 0, transform: 'translateY(4px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
};

function FilterPill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        appearance: 'none',
        border: `1px solid ${active ? INK : BORDER}`,
        bgcolor: active ? INK : SURFACE,
        color: active ? '#ffffff' : TEXT_SECONDARY,
        fontFamily: 'inherit',
        fontSize: 12.5,
        fontWeight: 500,
        letterSpacing: '0.01em',
        px: 1.5,
        py: 0.625,
        borderRadius: '999px',
        cursor: 'pointer',
        transition: 'background-color 120ms ease, border-color 120ms ease, color 120ms ease',
        '&:hover': active
          ? { bgcolor: '#000000' }
          : { borderColor: BORDER_HOVER, color: TEXT },
        '&:focus-visible': { outline: `2px solid ${INK}`, outlineOffset: 2 },
      }}
    >
      {children}
    </Box>
  );
}

function SectionDivider({
  label,
  count,
  accent,
  fg,
}: {
  label: string;
  count: number;
  accent: string;
  fg: string;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{ pt: 2.5, pb: 1, px: 0.25 }}
    >
      <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: accent, flexShrink: 0 }} />
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: fg,
          flexShrink: 0,
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.06em',
          color: TEXT_MUTED,
          flexShrink: 0,
        }}
      >
        · {count}
      </Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: BORDER }} />
    </Stack>
  );
}

type RowProps = {
  action: ActionDTO;
  bucket: Bucket;
  index: number;
  locale: string;
  showPersonalData: boolean;
  onMarkCompleted: (id: number) => void;
  pendingMark: boolean;
};

function ActionRow({
  action,
  bucket,
  index,
  locale,
  showPersonalData,
  onMarkCompleted,
  pendingMark,
}: RowProps) {
  const { t } = useTranslation();
  const history = useHistory();
  const meta = BUCKET_META[bucket];
  const isCompleted = bucket === 'completed';
  const description = action.description?.trim() || t('action.list.noDescription');
  const hasDescription = Boolean(action.description?.trim());

  // Right slot — each variant sizes to its own content. Equal 28px height
  // keeps the vertical rhythm; width follows the text.
  const rightSlot = isCompleted ? (
    <Stack
      direction="row"
      spacing={0.875}
      alignItems="center"
      sx={{
        height: 28,
        px: 1,
        borderRadius: '6px',
        border: `1px solid #d1fadf`,
        bgcolor: '#ecfdf5',
        flexShrink: 0,
      }}
    >
      <CheckCircleRoundedIcon sx={{ fontSize: 14, color: DONE_ACCENT }} />
      <Typography sx={{ fontSize: 12, fontWeight: 600, color: DONE_FG, whiteSpace: 'nowrap' }}>
        {t('action.list.completedOn')}
      </Typography>
      <Typography sx={{ fontSize: 12, color: DONE_FG, opacity: 0.75, whiteSpace: 'nowrap' }}>
        {formatRelative(action.completionDte, locale)}
      </Typography>
    </Stack>
  ) : (
    <Button
      onClick={e => {
        e.preventDefault();
        e.stopPropagation();
        if (action.id) onMarkCompleted(action.id);
      }}
      disabled={pendingMark}
      disableElevation
      startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
      sx={{
        bgcolor: INK,
        color: '#ffffff',
        fontSize: 12.5,
        fontWeight: 500,
        textTransform: 'none',
        height: 28,
        px: 1.25,
        py: 0,
        borderRadius: '6px',
        border: `1px solid ${INK}`,
        whiteSpace: 'nowrap',
        flexShrink: 0,
        minWidth: 'auto',
        '&:hover': { bgcolor: '#000000' },
        '&:disabled': { bgcolor: SURFACE_SUBTLE, color: TEXT_MUTED, borderColor: BORDER },
      }}
    >
      {t('action.mark.as.completed')}
    </Button>
  );

  const deadlineSubline = (() => {
    if (isCompleted) return null;
    if (bucket === 'today') return t('action.list.dueToday');
    if (bucket === 'overdue') {
      return `${formatRelative(action.deadlineDte, locale)} · ${t('action.list.overdueBy')}`;
    }
    if (action.deadlineDte) {
      return `${t('action.list.dueIn')} ${formatRelative(action.deadlineDte, locale)}`;
    }
    return null;
  })();

  // Staggered reveal, capped so a 200-row list doesn't crawl.
  const delay = Math.min(index, 24) * 30;

  return (
    <Box
      component={Link}
      to={`/actions/${action.id}`}
      sx={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        position: 'relative',
        bgcolor: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: '8px',
        overflow: 'hidden',
        opacity: 0,
        transform: 'translateY(4px)',
        animation: `actionRowFadeUp 360ms cubic-bezier(0.2, 0.7, 0.2, 1) ${delay}ms forwards`,
        '@keyframes actionRowFadeUp': fadeUpKeyframe,
        transition:
          'border-color 140ms ease, background-color 140ms ease, box-shadow 140ms ease',
        '&:hover': {
          borderColor: BORDER_HOVER,
          bgcolor: SURFACE_SUBTLE,
          boxShadow: '0 1px 0 rgba(17, 20, 24, 0.04)',
        },
        '&:focus-visible': {
          outline: 'none',
          borderColor: INK,
          boxShadow: `0 0 0 3px rgba(17, 20, 24, 0.08)`,
        },
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '3px',
          bgcolor: meta.accent,
        }}
      />
      <Stack
        direction="row"
        spacing={{ xs: 1.25, sm: 1.75 }}
        alignItems="center"
        sx={{ pl: { xs: 1.75, sm: 2.25 }, pr: { xs: 1.25, sm: 1.5 }, py: 1.25 }}
      >
        <Avatar
          sx={{
            width: 30,
            height: 30,
            fontSize: 11,
            fontWeight: 600,
            color: TEXT,
            bgcolor: avatarColor(action.assignedUser),
            border: `1px solid ${BORDER}`,
            flexShrink: 0,
          }}
        >
          {initials(action.assignedUser)}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 14.5,
              fontWeight: 500,
              color: hasDescription ? TEXT : TEXT_MUTED,
              fontStyle: hasDescription ? 'normal' : 'italic',
              lineHeight: 1.4,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              wordBreak: 'break-word',
            }}
          >
            {description}
          </Typography>
          {(((action.locations?.length ?? 0) > 0) || action.outOfBuilding) && (
            <Stack
              direction="row"
              spacing={0.625}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ mt: 0.5 }}
            >
              <PlaceOutlinedIcon
                sx={{ fontSize: 13, color: TEXT_MUTED, flexShrink: 0 }}
              />
              {action.locations?.map((loc, i) => (
                <Box
                  key={`${action.id}-loc-${i}`}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    height: 20,
                    px: 0.875,
                    borderRadius: '5px',
                    bgcolor: SURFACE_SUBTLE,
                    border: `1px solid ${BORDER}`,
                    fontSize: 11.5,
                    fontWeight: 500,
                    color: TEXT_SECONDARY,
                    maxWidth: 200,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {loc}
                </Box>
              ))}
              {action.outOfBuilding && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.375,
                    height: 20,
                    px: 0.75,
                    borderRadius: '5px',
                    bgcolor: TODAY_FG,
                    color: '#ffffff',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.02em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <WarningAmberRoundedIcon sx={{ fontSize: 11 }} />
                  {t('action.list.outsideBuilding')}
                </Box>
              )}
            </Stack>
          )}
          <Stack
            direction="row"
            spacing={0.875}
            alignItems="center"
            sx={{ mt: 0.5 }}
            divider={
              <Box
                aria-hidden
                sx={{
                  width: 2,
                  height: 2,
                  borderRadius: '50%',
                  bgcolor: TEXT_MUTED,
                  flexShrink: 0,
                }}
              />
            }
          >
            <Typography
              sx={{
                fontSize: 12.5,
                color: TEXT_SECONDARY,
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: 240,
              }}
            >
              {action.assignedUser
                ? fullName(action.assignedUser, showPersonalData)
                : t('action.list.unassigned')}
            </Typography>
            {action.createDte && (
              <Typography
                sx={{ fontSize: 12.5, color: TEXT_MUTED, whiteSpace: 'nowrap' }}
              >
                {t('action.list.createdAgo')} {formatRelative(action.createDte, locale)}
              </Typography>
            )}
            {deadlineSubline && (
              <Typography
                sx={{
                  fontSize: 12.5,
                  fontWeight: 500,
                  color: meta.fg,
                  whiteSpace: 'nowrap',
                }}
              >
                {deadlineSubline}
              </Typography>
            )}
            {(action.comments?.length ?? 0) > 0 && (
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                sx={{ flexShrink: 0 }}
              >
                <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 12, color: TEXT_MUTED }} />
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 500,
                    color: TEXT_SECONDARY,
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                  }}
                >
                  {action.comments?.length}
                </Typography>
              </Stack>
            )}
            <Typography sx={{ fontSize: 12, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>
              #{action.id}
            </Typography>
          </Stack>
        </Box>

        {rightSlot}

        {action.activityId != null && (
          <Tooltip title={t('action.list.openActivity')} arrow>
            <IconButton
              aria-label={t('action.list.openActivity').toString()}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                history.push(
                  Activity.children.activityDetails.route(
                    String(action.activityId),
                    Mode.VIEW,
                  ),
                );
              }}
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                color: TEXT_MUTED,
                border: `1px solid ${BORDER}`,
                borderRadius: '6px',
                '&:hover': {
                  color: INK,
                  borderColor: BORDER_HOVER,
                  bgcolor: SURFACE_SUBTLE,
                },
              }}
            >
              <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}

        <ArrowForwardRoundedIcon
          sx={{
            fontSize: 16,
            color: TEXT_MUTED,
            flexShrink: 0,
            opacity: 0.6,
            transition: 'transform 120ms ease, opacity 120ms ease',
            '.MuiBox-root:hover &': { transform: 'translateX(2px)', opacity: 1 },
          }}
        />
      </Stack>
    </Box>
  );
}

function SkeletonRow({ idx }: { idx: number }) {
  return (
    <Box
      sx={{
        bgcolor: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: '8px',
        px: 2.25,
        py: 1.25,
      }}
    >
      <Stack direction="row" spacing={1.75} alignItems="center">
        <Skeleton variant="circular" width={30} height={30} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant="text" width={`${60 + ((idx * 11) % 30)}%`} height={18} />
          <Skeleton variant="text" width={`${30 + ((idx * 13) % 25)}%`} height={14} />
        </Box>
        <Skeleton variant="rectangular" width={120} height={26} sx={{ borderRadius: '6px' }} />
      </Stack>
    </Box>
  );
}

type ContainerProps = {
  showPersonalData?: boolean;
};

const ActionsDetailsContainer = ({ showPersonalData = false }: ContainerProps) => {
  const { t } = useTranslation();
  const locale = i18n.language;
  const dispatch = useTitleDispatch();
  const { actions, isLoading } = useAllActions();
  const markAsCompleted = useMarkActionAsCompleted();

  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    dispatch({ pageTitle: t('page.all.actions.title') });
  }, [dispatch, t]);

  const handleMarkAsCompleted = (id: number) => {
    setPendingIds(prev => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    markAsCompleted(id);
  };

  // Filter + bucket grouping. Bucketization runs on the unfiltered list so the
  // summary counters (total / overdue / week) reflect the real world, not the
  // filtered view.
  const allBuckets = useMemo(() => {
    const map: Record<Bucket, ActionDTO[]> = {
      overdue: [],
      today: [],
      week: [],
      upcoming: [],
      completed: [],
    };
    (actions ?? []).forEach(a => {
      map[bucketFor(a)].push(a);
    });
    // Sort each bucket: pending → earliest deadline first; completed → most
    // recently completed first.
    map.overdue.sort((a, b) => +new Date(a.deadlineDte) - +new Date(b.deadlineDte));
    map.today.sort((a, b) => +new Date(a.deadlineDte) - +new Date(b.deadlineDte));
    map.week.sort((a, b) => +new Date(a.deadlineDte) - +new Date(b.deadlineDte));
    map.upcoming.sort(
      (a, b) =>
        +new Date(a.deadlineDte ?? '9999-12-31') - +new Date(b.deadlineDte ?? '9999-12-31')
    );
    map.completed.sort(
      (a, b) => +new Date(b.completionDte ?? 0) - +new Date(a.completionDte ?? 0)
    );
    return map;
  }, [actions]);

  const summary = {
    total: actions?.length ?? 0,
    overdue: allBuckets.overdue.length,
    week: allBuckets.week.length,
  };

  const visibleBuckets = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Allow users to type the action id either as "123" or "#123" — the
    // leading hash is stripped so it doesn't have to match literally.
    const idQuery = q.replace(/^#/, '');
    const matches = (a: ActionDTO): boolean => {
      if (!q) return true;
      const desc = (a.description ?? '').toLowerCase();
      const assignee = (a.assignedUser?.name ?? '').toLowerCase();
      const id = a.id != null ? String(a.id) : '';
      return (
        desc.includes(q) ||
        assignee.includes(q) ||
        (idQuery !== '' && id.includes(idQuery))
      );
    };
    const includeBucket = (b: Bucket): boolean => {
      if (filter === 'pending') return b !== 'completed';
      if (filter === 'completed') return b === 'completed';
      return true;
    };
    return BUCKET_ORDER.filter(includeBucket)
      .map(b => ({ bucket: b, items: allBuckets[b].filter(matches) }))
      .filter(g => g.items.length > 0);
  }, [allBuckets, filter, query]);

  const visibleCount = visibleBuckets.reduce((n, g) => n + g.items.length, 0);
  let rowIndex = 0;

  if (!isLoading && !actions) {
    return <ErrorPage />;
  }

  return (
    <Box sx={{ bgcolor: SURFACE, minHeight: '100%', py: { xs: 3, md: 4 } }}>
      <Container maxWidth="lg" sx={{ maxWidth: { md: 1032 } }}>
        <Stack spacing={3}>
          {/* — Header: title + summary + controls. Title and toolbar live on the
              same baseline at md+, stacked on xs. */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', md: 'flex-end' }}
            justifyContent="space-between"
          >
            <Box>
              <Typography
                component="h1"
                sx={{
                  fontSize: { xs: 24, md: 28 },
                  fontWeight: 700,
                  letterSpacing: '-0.015em',
                  color: TEXT,
                  lineHeight: 1.15,
                }}
              >
                {t('page.all.actions.title')}
              </Typography>
              <Stack
                direction="row"
                spacing={0.875}
                alignItems="center"
                sx={{ mt: 0.5 }}
                divider={
                  <Box
                    aria-hidden
                    sx={{
                      width: 2,
                      height: 2,
                      borderRadius: '50%',
                      bgcolor: TEXT_MUTED,
                    }}
                  />
                }
              >
                <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
                  {t('action.list.summary.total', { count: summary.total })}
                </Typography>
                {summary.overdue > 0 && (
                  <Typography sx={{ fontSize: 13, fontWeight: 500, color: OVERDUE_FG }}>
                    {t('action.list.summary.overdue', { count: summary.overdue })}
                  </Typography>
                )}
                {summary.week > 0 && (
                  <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY }}>
                    {t('action.list.summary.thisWeek', { count: summary.week })}
                  </Typography>
                )}
              </Stack>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ width: { xs: '100%', md: 'auto' }, flexWrap: 'wrap', rowGap: 1 }}
            >
              <Box
                sx={{
                  flex: { xs: 1, md: '0 0 auto' },
                  display: 'flex',
                  alignItems: 'center',
                  border: `1px solid ${BORDER}`,
                  borderRadius: '8px',
                  bgcolor: SURFACE,
                  width: { md: 300 },
                  transition: 'border-color 120ms ease',
                  '&:focus-within': { borderColor: INK },
                }}
              >
                <InputBase
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={t('action.list.search.placeholder')}
                  startAdornment={
                    <InputAdornment position="start" sx={{ ml: 1, color: TEXT_MUTED }}>
                      <SearchRoundedIcon sx={{ fontSize: 16 }} />
                    </InputAdornment>
                  }
                  sx={{
                    flex: 1,
                    fontSize: 13.5,
                    color: TEXT,
                    px: 1.25,
                    py: 0.625,
                    '& input::placeholder': { color: TEXT_MUTED, opacity: 1 },
                  }}
                />
              </Box>
              <Stack direction="row" spacing={0.5}>
                <FilterPill active={filter === 'all'} onClick={() => setFilter('all')}>
                  {t('action.list.filter.all')}
                </FilterPill>
                <FilterPill
                  active={filter === 'pending'}
                  onClick={() => setFilter('pending')}
                >
                  {t('action.list.filter.pending')}
                </FilterPill>
                <FilterPill
                  active={filter === 'completed'}
                  onClick={() => setFilter('completed')}
                >
                  {t('action.list.filter.completed')}
                </FilterPill>
              </Stack>
            </Stack>
          </Stack>

          {/* — Body: loading skeletons, empty state, or grouped feed. */}
          {isLoading ? (
            <Stack spacing={1}>
              {[0, 1, 2, 3].map(i => (
                <SkeletonRow key={i} idx={i} />
              ))}
            </Stack>
          ) : visibleCount === 0 ? (
            <Box
              sx={{
                border: `1px dashed ${BORDER}`,
                borderRadius: '10px',
                py: 5,
                px: 3,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ fontSize: 15, fontWeight: 600, color: TEXT, mb: 0.5 }}>
                {t('action.list.empty.title')}
              </Typography>
              <Typography sx={{ fontSize: 13, color: TEXT_MUTED }}>
                {t('action.list.empty.subtitle')}
              </Typography>
            </Box>
          ) : (
            <Box>
              {visibleBuckets.map(group => {
                const meta = BUCKET_META[group.bucket];
                return (
                  <Box key={group.bucket}>
                    <SectionDivider
                      label={t(meta.i18nKey)}
                      count={group.items.length}
                      accent={meta.accent}
                      fg={meta.fg}
                    />
                    <Stack spacing={1}>
                      {group.items.map(a => {
                        const idx = rowIndex;
                        rowIndex += 1;
                        return (
                          <ActionRow
                            key={a.id}
                            action={a}
                            bucket={group.bucket}
                            index={idx}
                            locale={locale}
                            showPersonalData={showPersonalData}
                            onMarkCompleted={handleMarkAsCompleted}
                            pendingMark={a.id ? pendingIds.has(a.id) : false}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                );
              })}
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

const mapStateToProps = (state: any) => ({
  showPersonalData: state.dataPrivacy?.showPersonalData ?? false,
});

export default connect(mapStateToProps, null)(ActionsDetailsContainer);
